import { useState, useRef, useEffect } from 'react';
import { exportConversationToPDF } from '../utils/exportPDF';
import { theme, spacing, typography, buttonStyles, mergeStyles, isMobile, mobileSpacing } from '../styles/theme';
import { trackUserEvent } from '../analytics';

export default function ExportDropdown({ messages, currentMessageIndex, userData, conversationTitle }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleExportAll = () => {
    const title = conversationTitle || messages[0]?.text?.substring(0, 50) || 'Judgy Conversation';
    exportConversationToPDF(messages, title);
    trackUserEvent('export_pdf', {
        type: 'full_conversation',
        message_count: messages.length
    });
    setIsOpen(false);
  };

  const handleExportSingle = () => {
    // Get the current Q&A pair
    // currentMessageIndex is the bot message, so we need the previous user message too
    const userMessage = messages[currentMessageIndex - 1];
    const botMessage = messages[currentMessageIndex];
    
    if (!userMessage || !botMessage) {
      alert('Could not export this Q&A pair');
      return;
    }

    const singleQA = [userMessage, botMessage];
    const title = conversationTitle || userMessage.text?.substring(0, 50) || 'Judgy Q&A';
    
    exportConversationToPDF(singleQA, title);
    trackUserEvent('export_pdf', {
      type: 'single_qa',
      message_count: 2
    });
    setIsOpen(false);
  };

  // Don't show if not premium
  if (!userData?.isPremium) return null;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Export button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={mergeStyles(
          buttonStyles.base,
          buttonStyles.small,
          {
            backgroundColor: 'transparent',
            color: theme.text.secondary,
            padding: `${spacing.xs} ${spacing.sm}`,
            fontSize: typography.fontSize.xs,
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
          },
          isMobile() && {
            padding: `${mobileSpacing.xs} ${mobileSpacing.sm}`,
            fontSize: typography.mobileFontSize.xs,
            minHeight: '48px',
          }
        )}
      >
        📄 Export
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          backgroundColor: 'white',
          border: `1px solid ${theme.border.light}`,
          borderRadius: '6px',
          boxShadow: theme.shadow.medium,
          zIndex: 1000,
          minWidth: '200px',
          marginTop: spacing.xs,
          ...(isMobile() && {
            minWidth: '180px',
          })
        }}>
          <button
            onClick={handleExportSingle}
            style={{
              width: '100%',
              padding: `${spacing.md} ${spacing.lg}`,
              border: 'none',
              backgroundColor: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: typography.fontSize.sm,
              color: theme.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              minHeight: '44px',
              ...(isMobile() && {
                padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
                fontSize: typography.mobileFontSize.sm,
                minHeight: '48px',
              })
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.bg.secondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            📄 This Q&A only
          </button>
          
          <div style={{
            height: '1px',
            backgroundColor: theme.border.light,
            margin: `0 ${spacing.sm}`,
          }} />
          
          <button
            onClick={handleExportAll}
            style={{
              width: '100%',
              padding: `${spacing.md} ${spacing.lg}`,
              border: 'none',
              backgroundColor: 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              fontSize: typography.fontSize.sm,
              color: theme.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              minHeight: '44px',
              ...(isMobile() && {
                padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
                fontSize: typography.mobileFontSize.sm,
                minHeight: '48px',
              })
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.bg.secondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            📋 Full conversation
          </button>
        </div>
      )}
    </div>
  );
}